import el from "@cypherpotato/el";
import { diffLines } from "diff";
import { Content } from "./layout";
import { CommandBar } from "./command-bar";
import { Button, ButtonRow } from "./button";

function renderLineNumber(num) {
    return el("span.diff-line-number", num !== null ? String(num) : '');
}

function InlineView(changes) {
    let leftNum = 0;
    let rightNum = 0;

    return el(".diff-inline-view",
        ...changes.flatMap(change => {
            const lines = change.value.replace(/\n$/, '').split('\n');

            return lines.map(line => {
                if (change.removed) {
                    leftNum++;
                    return el(".diff-line.diff-removed",
                        renderLineNumber(leftNum),
                        renderLineNumber(null),
                        el("span.diff-line-prefix", "-"),
                        el("span.diff-line-content", line)
                    );
                } else if (change.added) {
                    rightNum++;
                    return el(".diff-line.diff-added",
                        renderLineNumber(null),
                        renderLineNumber(rightNum),
                        el("span.diff-line-prefix", "+"),
                        el("span.diff-line-content", line)
                    );
                } else {
                    leftNum++;
                    rightNum++;
                    return el(".diff-line.diff-unchanged",
                        renderLineNumber(leftNum),
                        renderLineNumber(rightNum),
                        el("span.diff-line-prefix", " "),
                        el("span.diff-line-content", line)
                    );
                }
            });
        })
    );
}

function SideBySideView(changes) {
    const leftLines = [];
    const rightLines = [];
    let leftNum = 0;
    let rightNum = 0;

    changes.forEach(change => {
        const lines = change.value.replace(/\n$/, '').split('\n');

        lines.forEach(line => {
            if (change.removed) {
                leftNum++;
                leftLines.push({ num: leftNum, content: line, type: 'removed' });
            } else if (change.added) {
                rightNum++;
                rightLines.push({ num: rightNum, content: line, type: 'added' });
            } else {
                leftNum++;
                rightNum++;
                leftLines.push({ num: leftNum, content: line, type: 'unchanged' });
                rightLines.push({ num: rightNum, content: line, type: 'unchanged' });
            }
        });
    });

    const maxLen = Math.max(leftLines.length, rightLines.length);
    const rows = [];

    let li = 0, ri = 0;
    while (li < leftLines.length || ri < rightLines.length) {
        const left = leftLines[li];
        const right = rightLines[ri];

        if (left && left.type === 'unchanged' && right && right.type === 'unchanged') {
            rows.push({ left, right });
            li++;
            ri++;
        } else if (left && left.type === 'removed') {
            rows.push({ left, right: null });
            li++;
        } else if (right && right.type === 'added') {
            rows.push({ left: null, right });
            ri++;
        } else {
            if (left) {
                rows.push({ left, right: null });
                li++;
            }
            if (right) {
                rows.push({ left: null, right });
                ri++;
            }
        }
    }

    return el(".diff-side-by-side-view",
        el(".diff-side.diff-side-left",
            ...rows.map(row => {
                if (!row.left) {
                    return el(".diff-line.diff-empty",
                        renderLineNumber(null),
                        el("span.diff-line-content")
                    );
                }
                return el(`.diff-line.diff-${row.left.type}`,
                    renderLineNumber(row.left.num),
                    el("span.diff-line-content", row.left.content)
                );
            })
        ),
        el(".diff-side.diff-side-right",
            ...rows.map(row => {
                if (!row.right) {
                    return el(".diff-line.diff-empty",
                        renderLineNumber(null),
                        el("span.diff-line-content")
                    );
                }
                return el(`.diff-line.diff-${row.right.type}`,
                    renderLineNumber(row.right.num),
                    el("span.diff-line-content", row.right.content)
                );
            })
        )
    );
}

function countChanges(changes) {
    let added = 0;
    let removed = 0;

    changes.forEach(change => {
        const lines = change.value.replace(/\n$/, '').split('\n').length;
        if (change.added) added += lines;
        else if (change.removed) removed += lines;
    });

    return { added, removed };
}

export function DiffViewer(options = {}) {
    const textA = options.textA || '';
    const textB = options.textB || '';
    const viewMode = el.state.create(options.viewMode || 'inline');

    const changes = diffLines(textA, textB);
    const stats = countChanges(changes);

    if (!textA && !textB) {
        return el(".diff-viewer",
            el(".diff-empty-state", "No content to compare.")
        );
    }

    return el(".diff-viewer",
        CommandBar(
            {
                style: {
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%"
                }
            },
            el.state.listening([viewMode], () =>
                ButtonRow(
                    Button("Inline", {
                        classList: [viewMode.value === 'inline' && 'checked', 'check-button'],
                        onClick() { viewMode.value = 'inline'; }
                    }),
                    Button("Side-by-side", {
                        classList: [viewMode.value === 'side-by-side' && 'checked', 'check-button'],
                        onClick() { viewMode.value = 'side-by-side'; }
                    })
                )
            ),
            el(".diff-stats",
                stats.added > 0 && el("span.diff-stat-added", `+${stats.added}`),
                stats.removed > 0 && el("span.diff-stat-removed", `-${stats.removed}`)
            )
        ),
        el.state.listening([viewMode], () =>
            el(".diff-output",
                viewMode.value === 'inline'
                    ? InlineView(changes)
                    : SideBySideView(changes)
            )
        )
    );
}